"use client"

import { useTranslations } from "next-intl"
import { useMemo, useState } from "react"
import { Badge } from "~/components/legacy/common/badge"
import { Button } from "~/components/legacy/common/button"
import { Card } from "~/components/legacy/common/card"
import { H4, H6 } from "~/components/legacy/common/heading"
import { Link } from "~/components/legacy/common/link"
import { ExternalLinkIcon, RotateCcwIcon, Share2Icon, ShieldCheckIcon } from "~/components/ui/icons"
import { Grid } from "~/components/legacy/web/ui/grid"
import { CrawlerCard } from "./checker-crawler-card"
import type { GaugeKind } from "./checker-gauges"
import { Gauge, MiniGauge } from "./checker-gauges"
import {
  buildScoreSummary,
  getDirectiveCount,
  isSearchOpenAndTrainingBlocked,
  matchesFilter,
} from "./checker-scoring"
import type { CheckerPolicy, CheckerResponse, ResultFilter } from "./checker-types"

const filters: ResultFilter[] = ["all", "allow", "disallow"]

function gaugeKind(percent: number): GaugeKind {
  if (percent === 100) return "success"
  if (percent === 0) return "danger"
  return "warning"
}

function StatusCard({ response }: Readonly<{ response: CheckerResponse }>) {
  const t = useTranslations("pages.ai_crawler_checker.checker")
  const summary = buildScoreSummary(response)
  const directiveCount = getDirectiveCount(response.results)

  return (
    <Card hover={false} className="rounded-xl p-5">
      <div className="flex items-center gap-3">
        <span className="flex size-9 items-center justify-center rounded-lg bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">
          <ShieldCheckIcon className="size-5" aria-hidden />
        </span>
        <H4 as="strong" className="text-base">
          {t("status.fetched_for", { host: summary.host })}
        </H4>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" size="lg">
          {t("status.http", { status: response.fetchStatus })}
        </Badge>
        <Badge variant="outline" size="lg">
          {new Date(response.checkedAt).toLocaleString()}
        </Badge>
        <Badge variant="outline" size="lg" asChild>
          <Link href={response.robotsUrl} target="_blank" rel="noreferrer">
            robots.txt <ExternalLinkIcon />
          </Link>
        </Badge>
        {directiveCount > 0 && (
          <Badge variant="outline" size="lg">
            {t("status.directives", { count: directiveCount })}
          </Badge>
        )}
        {response.warnings.map(warning => (
          <Badge key={warning} variant="warning" size="lg">
            {warning === "robots_txt_not_found"
              ? t("warnings.robots_txt_not_found")
              : warning.replaceAll("_", " ")}
          </Badge>
        ))}
      </div>
    </Card>
  )
}

function getVerdictCopy(
  summary: ReturnType<typeof buildScoreSummary>,
  t: ReturnType<typeof useTranslations>,
) {
  if (summary.verdict === "open") {
    return {
      pill: t("score.pills.open"),
      text: t("score.text.open", { total: summary.total }),
      title: t("score.titles.open"),
    }
  }

  if (summary.verdict === "closed") {
    return {
      pill: t("score.pills.closed"),
      text: t("score.text.closed", { total: summary.total }),
      title: t("score.titles.closed"),
    }
  }

  return {
    pill: t("score.pills.partial"),
    text: t("score.text.partial", {
      allowed: summary.allowed,
      blocked: summary.blocked,
      total: summary.total,
    }),
    title: isSearchOpenAndTrainingBlocked(summary)
      ? t("score.titles.search_open_training_closed")
      : t("score.titles.partial"),
  }
}

function Scorecard({ response }: Readonly<{ response: CheckerResponse }>) {
  const t = useTranslations("pages.ai_crawler_checker.checker")
  const [copied, setCopied] = useState(false)
  const summary = buildScoreSummary(response)
  const verdict = getVerdictCopy(summary, t)

  async function share() {
    await navigator.clipboard?.writeText(globalThis.location.href)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  return (
    <Card hover={false} className="items-center gap-0 rounded-xl p-6 text-center sm:p-8">
      <Button
        variant="secondary"
        size="sm"
        prefix={<Share2Icon />}
        className="self-end"
        onClick={() => void share()}
      >
        {copied ? t("share.copied") : t("share.label")}
      </Button>
      <p className="mt-1 text-sm font-medium text-muted-foreground">{t("score.results_for")}</p>
      <p className="mt-1 break-all text-lg font-semibold">{summary.host}</p>
      <H4 as="h2" className="mt-3 text-2xl">
        {verdict.title}
      </H4>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">{verdict.text}</p>
      <Gauge percent={summary.percent} kind={summary.verdictKind} caption={t("summary.allowed")} />
      <Badge variant={summary.verdictKind} className="mt-4 rounded-full uppercase tracking-widest">
        {verdict.pill}
      </Badge>
      <div className="mt-7 h-px w-full bg-border" />
      <div className="mt-7 grid w-full grid-cols-2 gap-6 sm:grid-cols-4">
        {summary.categories.map(category => (
          <MiniGauge
            key={category.category}
            allowed={category.allowed}
            total={category.total}
            percent={category.percent}
            kind={gaugeKind(category.percent)}
            label={t(`categories.${category.category}`)}
          />
        ))}
      </div>
    </Card>
  )
}

function SummaryFilter({
  filter,
  response,
  setFilter,
}: Readonly<{
  filter: ResultFilter
  response: CheckerResponse
  setFilter: (filter: ResultFilter) => void
}>) {
  const t = useTranslations("pages.ai_crawler_checker.checker")
  const summary = buildScoreSummary(response)

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b pb-5">
      <span className="inline-flex items-baseline gap-2">
        <strong className="text-2xl text-green-600 dark:text-green-400">{summary.allowed}</strong>
        <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          {t("summary.allowed")}
        </span>
      </span>
      <span className="h-6 w-px bg-border" aria-hidden />
      <span className="inline-flex items-baseline gap-2">
        <strong className="text-2xl text-red-600 dark:text-red-400">{summary.blocked}</strong>
        <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          {t("summary.blocked")}
        </span>
      </span>
      <div className="ml-auto flex w-full rounded-lg border bg-card p-1 sm:w-auto">
        {filters.map(item => (
          <button
            key={item}
            type="button"
            aria-pressed={filter === item}
            className={
              filter === item
                ? "flex-1 rounded-md border bg-background px-3 py-1.5 text-sm font-medium"
                : "flex-1 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
            }
            onClick={() => setFilter(item)}
          >
            {t(`filters.${item}`)}
          </button>
        ))}
      </div>
    </div>
  )
}

function EmptyState({ filter, onReset }: Readonly<{ filter: ResultFilter; onReset: () => void }>) {
  const t = useTranslations("pages.ai_crawler_checker.checker")

  return (
    <div className="mt-8 flex flex-col items-center gap-2 rounded-xl border border-dashed bg-card p-10 text-center">
      <ShieldCheckIcon className="size-8 text-muted-foreground" aria-hidden />
      <H4 as="h3" className="text-base">
        {t(`empty.${filter}.title`)}
      </H4>
      <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
        {t(`empty.${filter}.description`)}
      </p>
      <Button
        type="button"
        variant="secondary"
        size="md"
        prefix={<RotateCcwIcon />}
        onClick={onReset}
      >
        {t("empty.reset")}
      </Button>
    </div>
  )
}

function CrawlerGroups({
  filter,
  response,
}: Readonly<{ filter: ResultFilter; response: CheckerResponse }>) {
  const t = useTranslations("pages.ai_crawler_checker.checker")
  const labels: Record<CheckerPolicy, string> = {
    allow: t("policies.allow"),
    disallow: t("policies.disallow"),
    unknown: t("policies.unknown"),
  }
  const summary = buildScoreSummary(response)

  return (
    <>
      {summary.categories.map(category => {
        const results = category.results.filter(result => matchesFilter(result, filter))
        if (!results.length) return null

        return (
          <section key={category.category} className="mt-8 flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <H6 as="h2">{t(`categories.${category.category}`)}</H6>
              <Badge variant="outline">{results.length}</Badge>
              <span className="text-sm text-muted-foreground">
                {t(`category_descriptions.${category.category}`)}
              </span>
            </div>
            <Grid className="gap-3">
              {results.map(result => (
                <CrawlerCard key={result.token} result={result} labels={labels} />
              ))}
            </Grid>
          </section>
        )
      })}
    </>
  )
}

export function CheckerResults({ response }: Readonly<{ response: CheckerResponse }>) {
  const t = useTranslations("pages.ai_crawler_checker")
  const [filter, setFilter] = useState<ResultFilter>("all")
  const visibleCount = useMemo(
    () => response.results.filter(result => matchesFilter(result, filter)).length,
    [filter, response.results],
  )

  return (
    <div className="flex flex-col gap-6">
      <StatusCard response={response} />
      <Scorecard response={response} />
      <SummaryFilter response={response} filter={filter} setFilter={setFilter} />
      {visibleCount > 0 ? (
        <CrawlerGroups response={response} filter={filter} />
      ) : (
        <EmptyState filter={filter} onReset={() => setFilter("all")} />
      )}
      <p className="text-xs leading-relaxed text-muted-foreground">{t("disclaimer")}</p>
    </div>
  )
}
